if (!customElements.get('onest-product')) {
  customElements.define('onest-product', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.mainForm = this.querySelector('[data-onest-form="main"]');
      this.variantSelect = this.mainForm?.querySelector('[data-variant-select]');
      this.quantity = this.mainForm?.elements.namedItem('quantity');
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-index]')];
      this.pending = false;
      this.querySelectorAll('[data-js-only]').forEach(node => { node.hidden = false; });
      this.classList.add('onest-enhanced');
      this.addEventListener('click', event => this.onClick(event), { signal: this.abort.signal });
      this.addEventListener('change', event => {
        if (event.target === this.variantSelect) this.updateVariant(true);
      }, { signal: this.abort.signal });
      this.addEventListener('submit', event => this.onSubmit(event), { signal: this.abort.signal });
      this.updateVariant(false);
      this.setupGallery();
      this.setupSticky();
      if (this.variantSelect) this.variantSelect.tabIndex = -1;
      window.Shopify?.PaymentButton?.init?.();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.galleryObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.abort = null;
    }

    updateVariant(updateURL) {
      const option = this.variantSelect?.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === 'true';
      this.querySelectorAll('[data-onest-price]').forEach(node => { node.textContent = option.dataset.money; });
      const compare = this.querySelector('[data-onest-compare]');
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
      const unit = this.querySelector('[data-onest-unit-price]');
      if (unit) unit.textContent = option.dataset.unitPrice ? ` · ${option.dataset.unitPrice}` : '';
      this.querySelectorAll('[data-variant-id]').forEach(button => {
        const matchingOption = [...this.variantSelect.options].find(item => item.value === button.dataset.variantId);
        button.setAttribute('aria-pressed', String(button.dataset.variantId === option.value));
        const description = button.querySelector('[data-pack-description]');
        if (description) description.textContent = matchingOption?.dataset.description || '';
      });
      this.querySelectorAll('[data-onest-add]').forEach(button => {
        button.disabled = this.pending || !available;
        button.textContent = available ? button.dataset.label : button.dataset.soldOut;
      });
      const payment = this.querySelector('[data-onest-payment]');
      if (payment) {
        payment.hidden = !available;
        payment.inert = !available;
      }
      if (this.quantity) {
        this.quantity.min = option.dataset.min || '1';
        this.quantity.step = option.dataset.step || '1';
        if (option.dataset.max) this.quantity.max = option.dataset.max;
        else this.quantity.removeAttribute('max');
        this.normalizeQuantity();
      }
      if (updateURL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    }

    normalizeQuantity() {
      if (!this.quantity) return;
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const entered = Number(this.quantity.value);
      const value = Number.isFinite(entered) ? entered : min;
      this.quantity.value = String(Math.min(max, min + Math.max(0, Math.round((value - min) / step)) * step));
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-variant-id') && this.variantSelect) {
        this.variantSelect.value = button.dataset.variantId;
        this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button.hasAttribute('data-quantity-step') && this.quantity) {
        this.normalizeQuantity();
        if (Number(button.dataset.quantityStep) > 0) this.quantity.stepUp();
        else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button.hasAttribute('data-gallery-index')) this.goToSlide(Number(button.dataset.galleryIndex));
      if (button.hasAttribute('data-gallery-prev')) this.goToSlide((this.activeSlide || 0) - 1);
      if (button.hasAttribute('data-gallery-next')) this.goToSlide((this.activeSlide || 0) + 1);
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    goToSlide(index) {
      if (!this.slides.length || !this.track) return;
      const wrapped = (index + this.slides.length) % this.slides.length;
      const slide = this.slides[wrapped];
      const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left, behavior: this.scrollBehavior() });
      this.setActiveSlide(wrapped);
    }

    setActiveSlide(index) {
      this.activeSlide = index;
      this.thumbs.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
    }

    setupGallery() {
      if (!this.track || this.slides.length < 2) return;
      this.galleryObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setActiveSlide(this.slides.indexOf(entry.target));
        }
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach(slide => this.galleryObserver.observe(slide));
      this.setActiveSlide(0);
    }

    setupSticky() {
      const sticky = this.querySelector('[data-onest-sticky]');
      if (!sticky || !this.mainForm) return;
      this.stickyObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      }, { threshold: 0 });
      this.stickyObserver.observe(this.mainForm);
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-onest-form]');
      if (!form || !window.fetch || event.submitter?.name !== 'add' || this.dataset.requiresSellingPlan === 'true') return;
      event.preventDefault();
      if (this.pending || !form.reportValidity()) return;
      this.pending = true;
      const errorNode = form.querySelector('[data-onest-error]');
      const buttons = [...form.querySelectorAll('button[type="submit"]'), ...this.querySelectorAll('[data-onest-add]')];
      buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
      try {
        const endpoint = new URL(this.dataset.addUrl || form.action, window.location.origin);
        if (endpoint.origin !== window.location.origin) throw new Error(this.dataset.cartError);
        if (!endpoint.pathname.endsWith('.js')) endpoint.pathname += '.js';
        const response = await fetch(endpoint, {
          method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'application/json' }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.dataset.cartError);
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: { product: data } }));
        const status = this.querySelector('[data-onest-status]');
        if (status) status.textContent = this.dataset.addedMessage;
        const cartTrigger = document.querySelector('[data-js-cart-icon]');
        if (document.querySelector('.quick-cart') && cartTrigger) cartTrigger.click();
        else window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (errorNode) {
          errorNode.textContent = error.message || this.dataset.cartError;
          errorNode.hidden = false;
        }
      } finally {
        this.pending = false;
        buttons.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
        this.updateVariant(false);
      }
    }
  });
}
